/**
 * Central Tool Configuration Registry
 *
 * This file is the single source of truth for all tool configurations
 * used throughout the application. Import from here instead of defining
 * tool configs locally in each page.
 *
 * Usage:
 *   import { TOOL_REGISTRY, GLOBAL_TOOLS, getSpaceTools } from '@/config/tools';
 */

import {
  Grid3x3,
  Users,
  ShieldCheck,
  Calendar,
  CheckSquare,
  MessageCircle,
  GraduationCap,
  FileText,
  MessageSquare,
  MapPin,
  Boxes,
  Award,
  CalendarCheck,
  GitBranch,
  ExternalLink,
  Globe,
  BarChart,
  LineChart,
  PieChart,
  Table,
  Database,
  Code,
  Terminal,
  Folder,
  Image,
  Video,
  Settings,
  Wrench,
  Zap,
  Rocket,
  Target,
} from 'lucide-react';

// =============================================================================
// CUSTOM TOOLS - User-defined iframe-based tools
// =============================================================================

/**
 * Prefix used for custom tool keys to distinguish them from built-in tools.
 */
export const CUSTOM_TOOL_PREFIX = 'custom_';

/**
 * Check if a tool key represents a custom tool.
 * @param {string} toolKey - The tool key to check
 * @returns {boolean} True if this is a custom tool key
 */
export const isCustomTool = (toolKey) => {
  return toolKey?.startsWith(CUSTOM_TOOL_PREFIX);
};

/**
 * Extract the custom tool ID from a custom tool key.
 * @param {string} toolKey - The custom tool key (e.g., 'custom_abc123')
 * @returns {string} The custom tool ID (e.g., 'abc123')
 */
export const getCustomToolId = (toolKey) => {
  return toolKey?.replace(CUSTOM_TOOL_PREFIX, '');
};

/**
 * Generate a custom tool key from a custom tool ID.
 * @param {string} customToolId - The custom tool ID
 * @returns {string} The custom tool key with prefix
 */
export const getCustomToolKey = (customToolId) => {
  return `${CUSTOM_TOOL_PREFIX}${customToolId}`;
};

/**
 * Available icons for custom tools.
 * Maps icon name strings to Lucide icon components.
 */
export const CUSTOM_TOOL_ICONS = {
  ExternalLink,
  Globe,
  BarChart,
  LineChart,
  PieChart,
  Table,
  Database,
  Code,
  Terminal,
  FileText,
  Folder,
  Image,
  Video,
  Map: MapPin,
  Calendar,
  Settings,
  Wrench,
  Zap,
  Rocket,
  Target,
};

/**
 * Get the icon component for a custom tool by name.
 * @param {string} iconName - The icon name
 * @returns {React.Component} The Lucide icon component
 */
export const getCustomToolIcon = (iconName) => {
  return CUSTOM_TOOL_ICONS[iconName] || ExternalLink;
};

/**
 * Preset colors available for custom tools.
 */
export const CUSTOM_TOOL_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
  '#a855f7', '#ec4899', '#f43f5e'
];

// =============================================================================
// SPACE TOOLS - Tools available within a space/project
// =============================================================================

/**
 * Registry of all space tools with their display configuration.
 * These tools can be enabled/disabled per space via the projects.tools JSON field.
 */
export const TOOL_REGISTRY = {
  matrix: {
    name: 'Matrix',
    color: '#ef4444',
    icon: Grid3x3,
  },
  team: {
    name: 'Team',
    color: '#3b82f6',
    icon: Users,
  },
  team_compliance: {
    name: 'Team Compliance',
    color: '#10b981',
    icon: ShieldCheck,
  },
  site_compliance: {
    name: 'Site Compliance',
    color: '#059669',
    icon: ShieldCheck,
  },
  bookings: {
    name: 'Bookings',
    color: '#a855f7',
    icon: Calendar,
  },
  calendar: {
    name: 'Calendar',
    color: '#ec4899',
    icon: Calendar,
  },
  checklists: {
    name: 'Tasks',
    color: '#10b981',
    icon: CheckSquare,
  },
  // Alias for future migration to 'tasks' key
  tasks: {
    name: 'Tasks',
    color: '#10b981',
    icon: CheckSquare,
  },
  chat: {
    name: 'Chat',
    color: '#6366f1',
    icon: MessageCircle,
  },
  qualifications: {
    name: 'Qualifications',
    color: '#f59e0b',
    icon: GraduationCap,
  },
  docs: {
    name: 'Docs',
    color: '#8b5cf6',
    icon: FileText,
  },
  message_board: {
    name: 'Posts',
    color: '#6366f1',
    icon: MessageSquare,
  },
  checkins: {
    name: 'Check-Ins',
    color: '#14b8a6',
    icon: MapPin,
  },
  // Legacy key - maps to same config as 'checkins'
  check_ins: {
    name: 'Check-Ins',
    color: '#14b8a6',
    icon: MapPin,
  },
  attendance: {
    name: 'Attendance',
    color: '#8b5cf6',
    icon: CalendarCheck,
  },
};

/**
 * Default order for space tools when no user preference is set.
 * Tools not in this list will appear at the end in their original order.
 */
export const DEFAULT_TOOL_ORDER = [
  'matrix',
  'team',
  'qualifications',
  'team_compliance',
  'site_compliance',
  'bookings',
  'message_board',
  'tasks',
  'checklists', // Legacy - kept for backwards compatibility
  'docs',
  'checkins',
  'calendar',
  'chat',
  'attendance',
];

// =============================================================================
// GLOBAL TOOLS - Company-wide navigation tools
// =============================================================================

/**
 * Global tools available at the company level.
 * These are always visible and don't depend on space configuration.
 */
export const GLOBAL_TOOLS = [
  {
    id: 'spaces',
    tool_type: 'spaces',
    name: 'Spaces',
    icon: Boxes,
    color: '#6366f1',
    route: '/dashboard',
  },
  {
    id: 'users',
    tool_type: 'users',
    name: 'Users',
    icon: Users,
    color: '#10b981',
    route: '/company-team',
  },
  {
    id: 'qualifications',
    tool_type: 'qualifications',
    name: 'Qualifications',
    icon: Award,
    color: '#f59e0b',
    route: '/qualifications',
  },
  {
    id: 'trainers',
    tool_type: 'trainers',
    name: 'Trainers',
    icon: GraduationCap,
    color: '#3b82f6',
    route: '/trainers',
  },
  {
    id: 'attendance',
    tool_type: 'attendance',
    name: 'Attendance',
    icon: CalendarCheck,
    color: '#8b5cf6',
    route: '/attendance',
  },
  {
    id: 'posts',
    tool_type: 'posts',
    name: 'Posts',
    icon: MessageSquare,
    color: '#ec4899',
    route: '/posts',
  },
  {
    id: 'processes',
    tool_type: 'processes',
    name: 'Processes',
    icon: GitBranch,
    color: '#14b8a6',
    route: '/processes',
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Convert a space's tools configuration object into an array of tool objects
 * with full display configuration.
 *
 * @param {Object} toolsConfig - The tools object from projects.tools (e.g., { matrix: true, team: true })
 * @returns {Array} Array of tool objects with id, tool_type, name, color, icon
 *
 * @example
 * const tools = getSpaceTools(space.tools);
 * // Returns: [{ id: 'matrix', tool_type: 'matrix', name: 'Matrix', color: '#ef4444', icon: Grid3x3 }, ...]
 */
export const getSpaceTools = (toolsConfig) => {
  if (!toolsConfig) return [];

  const config = typeof toolsConfig === 'string' ? JSON.parse(toolsConfig) : toolsConfig;

  return Object.entries(config)
    .filter(([key, enabled]) => enabled === true && TOOL_REGISTRY[key])
    .map(([toolKey]) => ({
      id: toolKey,
      tool_type: toolKey,
      name: TOOL_REGISTRY[toolKey]?.name || toolKey,
      color: TOOL_REGISTRY[toolKey]?.color,
      icon: TOOL_REGISTRY[toolKey]?.icon,
    }));
};

/**
 * Sort tools according to a preferred order, with any unordered tools at the end.
 *
 * @param {Array} tools - Array of tool objects with id property
 * @param {Array} preferredOrder - Array of tool IDs in preferred order (defaults to DEFAULT_TOOL_ORDER)
 * @returns {Array} Sorted array of tools
 */
export const sortToolsByOrder = (tools, preferredOrder = DEFAULT_TOOL_ORDER) => {
  if (!tools || tools.length === 0) return [];

  const orderedTools = [];
  const unorderedTools = [];

  // First, add tools that are in the preferred order
  preferredOrder.forEach((toolId) => {
    const tool = tools.find((t) => t.id === toolId || t.tool_type === toolId);
    if (tool) {
      orderedTools.push(tool);
    }
  });

  // Then, add any tools not in the preferred order
  tools.forEach((tool) => {
    const toolId = tool.id || tool.tool_type;
    if (!preferredOrder.includes(toolId)) {
      unorderedTools.push(tool);
    }
  });

  return [...orderedTools, ...unorderedTools];
};

/**
 * Get a single tool's configuration by its key.
 *
 * @param {string} toolKey - The tool identifier (e.g., 'matrix', 'team')
 * @returns {Object|null} Tool configuration or null if not found
 */
export const getToolConfig = (toolKey) => {
  return TOOL_REGISTRY[toolKey] || null;
};

/**
 * Get the route for a tool based on context.
 *
 * @param {string} toolType - The tool type identifier
 * @param {Object} options - Route options
 * @param {string} options.projectId - Project ID for space routes
 * @param {string} options.stackId - Stack ID for stack routes
 * @param {boolean} options.isGlobalView - Whether this is a global view
 * @param {boolean} options.isStackView - Whether this is a stack view
 * @returns {string} The route path
 */
export const getToolRoute = (toolType, { projectId, stackId, isGlobalView, isStackView, customToolData } = {}) => {
  // Handle custom tools
  if (isCustomTool(toolType)) {
    const customToolId = getCustomToolId(toolType);
    const slug = customToolData?.slug || customToolId;
    if (projectId) {
      return `/space/${projectId}/tool/${slug}`;
    }
    return '/dashboard';
  }

  // Global routes
  if (isGlobalView) {
    const globalRoutes = {
      spaces: '/dashboard',
      home: '/dashboard',
      team: '/company-team',
      users: '/company-team',
      qualifications: '/qualifications',
      trainers: '/trainers',
      attendance: '/attendance',
      message_board: '/posts',
      posts: '/posts',
      processes: '/processes',
    };
    return globalRoutes[toolType] || '/dashboard';
  }

  // Stack routes
  if (isStackView && stackId) {
    const stackRoutes = {
      matrix: `/stack/${stackId}/matrix`,
      team: `/stack/${stackId}/team`,
      qualifications: `/stack/${stackId}/qualifications`,
      bookings: `/stack/${stackId}/bookings`,
      calendar: `/stack/${stackId}/schedule`,
      tasks: `/stack/${stackId}/tasks`,
      checklists: `/stack/${stackId}/tasks`,
      docs: `/stack/${stackId}/docs`,
      message_board: `/stack/${stackId}/message-board`,
      check_ins: `/stack/${stackId}/checkins`,
      checkins: `/stack/${stackId}/checkins`,
    };
    return stackRoutes[toolType] || `/stack/${stackId}`;
  }

  // Space routes
  if (projectId) {
    const spaceRoutes = {
      matrix: `/space/${projectId}/matrix`,
      team: `/space/${projectId}/team`,
      compliance: `/space/${projectId}/compliance`,
      team_compliance: `/space/${projectId}/compliance`,
      site_compliance: `/space/${projectId}/site-compliance`,
      bookings: `/space/${projectId}/bookings`,
      qualifications: `/space/${projectId}/qualifications`,
      chat: `/chat?space=${projectId}`,
      message_board: `/space/${projectId}/message-board`,
      check_ins: `/space/${projectId}/checkins`,
      checkins: `/space/${projectId}/checkins`,
      calendar: `/space/${projectId}/schedule`,
      tasks: `/space/${projectId}/tasks`,
      checklists: `/space/${projectId}/tasks`,
      docs: `/space/${projectId}/docs`,
      attendance: `/space/${projectId}/attendance`,
    };
    return spaceRoutes[toolType] || `/space/${projectId}`;
  }

  return '/dashboard';
};

/**
 * Normalize a tool key to handle legacy naming inconsistencies.
 * e.g., 'check_ins' -> 'checkins'
 *
 * @param {string} toolKey - The tool key to normalize
 * @returns {string} Normalized tool key
 */
export const normalizeToolKey = (toolKey) => {
  const keyMap = {
    check_ins: 'checkins',
    // Add other legacy mappings here as needed
  };
  return keyMap[toolKey] || toolKey;
};
